import React, { useCallback, useEffect, useMemo } from 'react';
import FormEdit from '../../../../core/components/form/FormEdit';
import ValidInputMultiSelect from '../../../../core/components/validation/ValidInputMultiSelect';
import Button from '../../../../core/components/Button';
import { useFeedsSearchState } from './useFeedsSearchState';
import FormSearch from '../../../../core/components/FormSearch';
import { useFeedsSearchInit } from './useFeedsSearchInit';
import { useFeedsStoreActions } from '../pagehooks/useFeedsStore';
import { useAtomValue } from 'jotai';
import { feedsPageActionAtom, feedsUserContextAtom } from '../feeds_list_atoms';
import DateRangeController from '../../../../core/components/DateRangeController';
import {
    FEEDS_FEATURE_DATE_RANGE_MODES,
    FEEDS_FEATURE_LIMITED_DATE_RANGE_MODES,
} from '../../../../core/utils/dateRangeModesByFeature.const';
import { isAllSelected } from './cleanFeedsSearchPayload';
import { useDateRangeControllerState } from '../../../../core/hooks/useDateRangeControllerState';
import type { DateRangeMode } from '../../../../core/components/validation/dateRangeModesConfig';
import { getUiModeForFeeds } from '../pagehooks/useFeedsInit';
import { parseLocalDate } from '../../../../core/components/validation/ValidInputDate.utils';

type FeedsListSearchProps = {
    onEditSearch?: () => void;
};

const FeedsListSearch: React.FC<FeedsListSearchProps> = ({ onEditSearch }) => {
    const action = useAtomValue(feedsPageActionAtom);
    const init = useFeedsSearchInit();
    const drawerOpen = action == 'QUERY' || init;
    return drawerOpen ? <FeedsListForm disabled={init} /> : <FeedsListFormSearch onEditSearch={onEditSearch} />;
};

type FeedListFormProps = {
    disabled?: boolean;
};

const FeedsListForm = ({ disabled }: FeedListFormProps): JSX.Element => {
    const { userType } = useAtomValue(feedsUserContextAtom);
    const { state, setProperty, setMultipleProperties, handleSearch } = useFeedsSearchState();

    // Date range state from global form state
    const dateRangeMode = typeof state.DateRangeMode?.val === 'string' ? state.DateRangeMode.val : 'TODAY';
    const dateRangeStart = state.StartDate?.val ? parseLocalDate(state.StartDate.val) : new Date();
    const dateRangeEnd = state.EndDate?.val ? parseLocalDate(state.EndDate.val) : new Date();

    // Multi-select handlers
    const handleFacilityOptionSelect = useCallback((val: (string | number)[]) => setProperty('CE', val), [setProperty]);
    const handlePharmacyOptionSelect = useCallback((val: (string | number)[]) => setProperty('RX', val), [setProperty]);
    const handleFeedTypeOptionSelect = useCallback(
        (val: (string | number)[]) => setProperty('FeedType', val),
        [setProperty],
    );
    const handleStatusOptionSelect = useCallback(
        (val: (string | number)[]) => setProperty('Status', val),
        [setProperty],
    );
    const handleSourceOptionSelect = useCallback(
        (val: (string | number)[]) => setProperty('Source', val),
        [setProperty],
    );

    const {
        monthlyMonth,
        monthlyYear,
        qtr,
        qtrYear,
        handleModeChange,
        handleStartChange,
        handleEndChange,
        handleMonthlyChange,
        handleQuarterChange,
        startError,
        endError,
    } = useDateRangeControllerState(dateRangeMode, dateRangeStart, dateRangeEnd, setProperty, setMultipleProperties);

    // Patch: Provide setLocalError to DateRangeController handlers
    const handleStartChangeWithError = useCallback(
        (date: Date, setLocalError?: (msg: string) => void): void => {
            handleStartChange(date);
            if (setLocalError) setLocalError(''); // Optionally clear error here, or let hook handle it
        },
        [handleStartChange],
    );

    const handleEndChangeWithError = useCallback(
        (date: Date, setLocalError?: (msg: string) => void): void => {
            handleEndChange(date);
            if (setLocalError) setLocalError('');
        },
        [handleEndChange],
    );

    const allCEsOrAllFeedTypesSelected = useMemo(() => {
        if (disabled) return true;
        const ceOptions = state.CE.options?.map(opt => opt.val) || [];
        const feedTypeOptions = state.FeedType.options?.map(opt => opt.val) || [];
        const allCEsAreSelected = isAllSelected(state.CE.val, ceOptions);
        const allFeedTypesAreSelected = isAllSelected(state.FeedType.val, feedTypeOptions);
        return allCEsAreSelected && allFeedTypesAreSelected && ceOptions.length !== 1;
    }, [state.CE, state.FeedType, disabled]);

    const allowedModes = allCEsOrAllFeedTypesSelected
        ? FEEDS_FEATURE_LIMITED_DATE_RANGE_MODES
        : FEEDS_FEATURE_DATE_RANGE_MODES;

    // Reset date range mode to TODAY when allowedModes changes
    useEffect(() => {
        if (dateRangeMode !== 'TODAY' && allowedModes && !allowedModes.includes(dateRangeMode as DateRangeMode)) {
            const today = new Date();
            const yyyy = today.getFullYear();
            const mm = String(today.getMonth() + 1).padStart(2, '0');
            const dd = String(today.getDate()).padStart(2, '0');
            const todayStr = `${yyyy}-${mm}-${dd}`;
            setMultipleProperties({ DateRangeMode: 'TODAY', StartDate: todayStr, EndDate: todayStr });
        }
    }, [allowedModes, dateRangeMode, setMultipleProperties]);

    // Memoize options arrays for each dropdown (move out of render)
    const ceOptions = useMemo(() => {
        const result = state.CE.options;
        return result;
    }, [state.CE.options]);

    const rxOptions = useMemo(() => {
        const result = state.RX.options;
        return result;
    }, [state.RX.options]);

    const feedTypeOptions = useMemo(() => {
        const result = state.FeedType.options;
        return result;
    }, [state.FeedType.options]);

    const statusOptions = useMemo(() => {
        const result = state.Status.options;
        return result;
    }, [state.Status.options]);

    const sourceOptions = useMemo(() => {
        const result = state.Source.options;
        return result;
    }, [state.Source.options]);

    // Prepare props for dropdowns outside of render
    const ceProps = useMemo(() => ({ ...state.CE, options: ceOptions }), [state.CE, ceOptions]);
    const rxProps = useMemo(() => ({ ...state.RX, options: rxOptions }), [state.RX, rxOptions]);
    const feedTypeProps = useMemo(
        () => ({ ...state.FeedType, options: feedTypeOptions }),
        [state.FeedType, feedTypeOptions],
    );
    const statusProps = useMemo(() => ({ ...state.Status, options: statusOptions }), [state.Status, statusOptions]);
    const sourceProps = useMemo(() => ({ ...state.Source, options: sourceOptions }), [state.Source, sourceOptions]);

    return (
        <FormEdit
            formName="feedSearch"
            cssName="feed-search"
            fieldsetId="feed-search-fieldset"
        >
            <div className="fieldset-form">
                <div className="fieldset-inline">
                    <div className="fieldset-section">
                        <ValidInputMultiSelect
                            id="ce-select"
                            prop={ceProps}
                            onOptionSelect={handleFacilityOptionSelect}
                            placeholder="Select Facilities"
                        />
                        {userType === 'CE' ? null : (
                            <ValidInputMultiSelect
                                id="rx-select"
                                prop={rxProps}
                                onOptionSelect={handlePharmacyOptionSelect}
                                placeholder="Select Pharmacies"
                                labelNoMinWidth
                            />
                        )}
                    </div>
                    {userType === 'CE' ? null : (
                        <div className="fieldset-section">
                            <ValidInputMultiSelect
                                id="feedtype-select"
                                prop={feedTypeProps}
                                onOptionSelect={handleFeedTypeOptionSelect}
                                placeholder="Select Feed Types"
                                textInputWidth={370}
                            />
                            <ValidInputMultiSelect
                                id="source-select"
                                prop={sourceProps}
                                onOptionSelect={handleSourceOptionSelect}
                                placeholder="Select Source"
                                labelNoMinWidth
                            />
                            <ValidInputMultiSelect
                                id="status-select"
                                prop={statusProps}
                                onOptionSelect={handleStatusOptionSelect}
                                placeholder="Select Status"
                                labelNoMinWidth
                            />
                        </div>
                    )}
                </div>
                <div className="fieldset-inline">
                    <DateRangeController
                        allowedModes={allowedModes}
                        mode={dateRangeMode}
                        start={dateRangeStart}
                        end={dateRangeEnd}
                        monthlyMonth={monthlyMonth}
                        monthlyYear={monthlyYear}
                        qtr={qtr}
                        qtrYear={qtrYear}
                        onModeChange={handleModeChange}
                        onStartChange={handleStartChangeWithError}
                        onEndChange={handleEndChangeWithError}
                        onMonthlyChange={handleMonthlyChange}
                        onQuarterChange={handleQuarterChange}
                        startError={startError}
                        endError={endError}
                        disabled={disabled}
                    />
                </div>
                <div className="form-field">
                    <Button
                        onClick={handleSearch}
                        cssName="btn-submit"
                    >
                        Search
                    </Button>
                    <p className="facility-feed-types-warning-message">
                        <i>
                            {disabled || allCEsOrAllFeedTypesSelected
                                ? 'Choose a Facility or Feed Type for more date range options.'
                                : ''}
                        </i>
                    </p>
                </div>
            </div>
        </FormEdit>
    );
};

type FeedsListFormSearchProps = {
    onEditSearch?: () => void;
};

const FeedsListFormSearch: React.FC<FeedsListFormSearchProps> = ({ onEditSearch }) => {
    const { setAction } = useFeedsStoreActions();
    const { state } = useFeedsSearchState();
    const uiMode = useMemo(() => getUiModeForFeeds(), []);
    const isCE = uiMode === 'CE';

    /* eslint-disable-next-line */
    const { Status, Source, RX, FeedType, ...rest } = state;
    const modifiedState = isCE ? rest : state;

    const handleEdit = useCallback(() => {
        if (onEditSearch) onEditSearch();
        setTimeout(() => setAction('QUERY'), 500);
    }, [onEditSearch, setAction]);

    return (
        <FormSearch
            criteria={modifiedState}
            onEdit={handleEdit}
        />
    );
};

export default FeedsListSearch;
